/**
 * Fælles tal-/farve-hjælpere for marketing-siderne ([D-183]).
 *
 * Lå tidligere inline i PerformanceView.vue. Efter opdelingen i Oversigt +
 * kanal-undersider SKAL formateringen være ét sted, så et tal ser ens ud
 * uanset hvilken underside det står på (samme princip som UI-konsistens-
 * reglen: ændringer føres igennem HELE UI'et, ikke pr. side).
 */

import { yoyChange } from '@cockpit/shared';

import { isBrandUi } from '../composables/useBrandUi';
import {
  BRAND_NEG_DARK,
  BRAND_NEG_LIGHT,
  BRAND_POS_DARK,
  BRAND_POS_LIGHT,
  BRAND_RAMP_DARK,
  BRAND_RAMP_LIGHT,
  BRAND_SEQ_DARK,
  BRAND_SEQ_LIGHT,
} from './brand-chart';

/** Kvotient m. præcis én decimal (ROMS/MER/~ROAS). '—' ved manglende tal. */
export function ratio(n: number | null | undefined): string {
  return n == null
    ? '—'
    : n.toLocaleString('da-DK', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
}

/** Hele kroner m. suffiks. '—' ved manglende tal. */
export function kr(n: number | null | undefined): string {
  return n == null ? '—' : `${Math.round(n).toLocaleString('da-DK')} kr`;
}

/** Kort kr-form til akse-labels: 1.250.000 → "1,3 mio.", 42.000 → "42 t.". */
export function krShort(n: number): string {
  if (Math.abs(n) >= 1_000_000)
    return `${(n / 1_000_000).toLocaleString('da-DK', { maximumFractionDigits: 1 })} mio.`;
  if (Math.abs(n) >= 1_000) return `${Math.round(n / 1_000).toLocaleString('da-DK')} t.`;
  return `${Math.round(n)}`;
}

/**
 * Procent-format: højst ÉN decimal, og decimalen vises kun når den er ≠ 0.
 * Værdier er allerede procent-tal medmindre andet er noteret ved kaldstedet.
 */
export const pctNumFmt = new Intl.NumberFormat('da-DK', {
  minimumFractionDigits: 0,
  maximumFractionDigits: 1,
});

/** Andel som procent — '—' når nævneren er 0. */
export function pct(part: number, total: number): string {
  return total > 0 ? `${pctNumFmt.format((100 * part) / total)} %` : '—';
}

/**
 * % delta a vs b (null hvis b mangler/er 0).
 *
 * Delegerer til den fælles `yoyChange` ([D-201]) — regnestykket er identisk
 * med det der stod her, men det bor nu ét sted sammen med budgetsidens og
 * SMS'ens, så de tre ikke kan drifte fra hinanden igen. Bemærk at nævneren er
 * |b|: så er fortegnet på ændringen retvisende, også når b er negativ.
 */
export function deltaPct(a: number | null, b: number | null): number | null {
  return yoyChange(a, b).pct;
}

/** 'YYYY-MM' året før. */
export function priorYearMonth(m: string): string {
  return `${Number(m.slice(0, 4)) - 1}${m.slice(4)}`;
}

/**
 * Læser en CSS-variabel som rgb()-farve (tema-følsom).
 *
 * ⚠ KOMMAER ER IKKE VALGFRIE. Tokens står som mellemrums-separerede tripler
 * ("99 99 99", CSS Color Level 4), og `rgb(99 99 99)` er fuldt gyldig CSS —
 * men ECharts' egen farve-parser forstår det IKKE. Den logger "illegal color,
 * fallback to '#000000'" som en warning og tegner sort, så akser og grid-linjer
 * blev sorte i stedet for nedtonede uden at det så ud som en fejl (fundet
 * 27-08-2026 under [D-251]). Derfor normaliseres tripler til komma-formen, som
 * begge parsere forstår.
 */
export function cssVar(name: string): string {
  const raw = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  if (!raw) return '#888';
  // Kun rene mellemrums-tripler røres; alt andet (hex, rgb(), farvenavne)
  // sendes videre uændret.
  return /^\d+\s+\d+\s+\d+$/.test(raw) ? `rgb(${raw.split(/\s+/).join(', ')})` : `rgb(${raw})`;
}

export interface ChartPalette {
  muted: string;
  faint: string;
  grid: string;
  border: string;
  ramp: string[];
  /** Hovedlinjen i grafer med én serie (monokromt: tekstfarven). */
  ink: string;
  /** Rangorden mørk til lys (fx SERP-positioner). Monokromt = rampens gamle
   *  rækkefølge for den brug, så intet ændrer sig uden brand-udtrykket. */
  seq: string[];
  /** Op/ned-farver (over/under budget, vækst/fald) som hex. */
  pos: string;
  neg: string;
}

/** Chart-paletten — samme rampe på alle sider. Monokrom ([D-021]); med
 *  brand-udtrykket ([D-471]) designmanualens farver i validerede trin. Læser
 *  isBrandUi, så en computed der kalder den, tegner om ved skift. */
export function chartPalette(dark: boolean): ChartPalette {
  const base = { muted: cssVar('--muted'), faint: cssVar('--faint'), grid: cssVar('--chart-grid'), border: cssVar('--border') };
  if (isBrandUi.value) {
    const ramp = dark ? BRAND_RAMP_DARK : BRAND_RAMP_LIGHT;
    return {
      ...base,
      ramp,
      ink: ramp[0]!,
      seq: dark ? BRAND_SEQ_DARK : BRAND_SEQ_LIGHT,
      pos: dark ? BRAND_POS_DARK : BRAND_POS_LIGHT,
      neg: dark ? BRAND_NEG_DARK : BRAND_NEG_LIGHT,
    };
  }
  const ramp = dark
    ? ['#f5f5f5', '#9e9e9e', '#c9c9c9', '#777777', '#565656', '#3d3d3d']
    : ['#171717', '#777777', '#4d4d4d', '#9e9e9e', '#bcbcbc', '#d4d4d4'];
  return {
    ...base,
    ramp,
    ink: cssVar('--text'),
    seq: [ramp[0]!, ramp[2]!, ramp[1]!, ramp[4]!, ramp[5]!],
    // Monokromt: de faste hex-farver graferne brugte før tokens ([D-471]).
    pos: dark ? '#86EFAC' : '#16A34A',
    neg: dark ? '#FCA5A5' : '#DC2626',
  };
}
